import { Injectable } from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { AuthenticationService } from '../authentication.service';
import { environment } from "src/environments/environment";

@Injectable()
export class ApiInterceptor implements HttpInterceptor {

    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(
        private api: ApiService,
        private authService: AuthenticationService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // Skip Google API
        if (req.url.includes('maps.googleapis.com')) {
            return next.handle(req);
        }

        let token = this.api.GetToken();

        let headers: any = {
            'u-jacaso-key': environment.appKey,
            'Content-Type': 'application/json'
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const clonedRequest = req.clone({
            setHeaders: headers
        });

        return next.handle(clonedRequest).pipe(
            catchError((error: HttpErrorResponse) => {

                if (error.status === 401 && !req.url.includes('refreshtoken')) {
                    return this.handle401Error(req, next);
                }

                return throwError(() => error);
            })
        );
    }

    private handle401Error(req: HttpRequest<any>, next: HttpHandler) {

        if (!this.isRefreshing) {

            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.api.GenerateRefreshToken().pipe(

                switchMap((res: any) => {

                    const newToken = res?.token || res?.data?.token;

                    if (!newToken) {
                        this.forceLogout();
                        return throwError(() => new Error('Refresh token invalid'));
                    }

                    this.api.SaveTokens(res);

                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(newToken);

                    return next.handle(
                        req.clone({
                            setHeaders: {
                                'u-jacaso-key': environment.appKey,
                                'Authorization': `Bearer ${newToken}`,
                                'Content-Type': 'application/json'
                            }
                        })
                    );
                }),

                catchError((err) => {
                    this.forceLogout();
                    return throwError(() => err);
                })
            );

        } else {

            return this.refreshTokenSubject.pipe(
                filter(token => token != null),
                take(1),
                switchMap(token => {
                    return next.handle(
                        req.clone({
                            setHeaders: {
                                'u-jacaso-key': environment.appKey,
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            }
                        })
                    );
                }),
                catchError(err => {
                    return throwError(() => err);
                })
            );
        }
    }

    private forceLogout() {

        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);

        this.api.onLogOut();
        this.authService.logout();

        // Optional: small delay to avoid Angular navigation race
        setTimeout(() => {
            window.location.href = '/login';
        }, 100);
    }
}